import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, UserPlus, X, MailCheck } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

export const TeamInvitePanel = ({ team, reload }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/portal/my-teams/${team.id}/member-search`, { params: { q } }).then(({ data }) => setResults(data)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [q, team.id]);
  const invite = (m) => api.post(`/portal/my-teams/${team.id}/invites`, { member_id: m.id })
    .then(() => { toast.success(`Invitation sent to ${m.name}`); setResults((r) => r.map((x) => (x.id === m.id ? { ...x, invited: true } : x))); reload(); }).catch(err);
  const cancel = (i) => api.delete(`/portal/my-teams/${team.id}/invites/${i.id}`).then(() => { toast.success("Invitation cancelled"); reload(); }).catch(err);

  return (
    <div data-testid={`team-invite-${team.id}`}>
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--sj-text-3)" }} />
        <input data-testid={`team-invite-search-${team.id}`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search church members by name…"
          className="w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/20 pl-9 pr-3 py-2 text-sm" />
      </div>
      {results.length > 0 && (
        <div className="mt-2 space-y-1 max-h-56 overflow-y-auto">
          {results.map((m) => (
            <div key={m.id} className="flex items-center gap-2 p-2 rounded-lg" style={{ background: "var(--sj-bg)" }} data-testid={`team-invite-result-${m.id}`}>
              <span className="text-sm flex-1 min-w-0 truncate">{m.name}</span>
              {m.invited ? <span className="text-xs font-semibold" style={{ color: "var(--sj-text-3)" }}>Invited</span> : (
                <button data-testid={`team-invite-btn-${m.id}`} onClick={() => invite(m)} className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 text-white" style={{ background: "var(--sj-primary)" }}><UserPlus className="h-3.5 w-3.5" /> Invite</button>
              )}
            </div>
          ))}
        </div>
      )}
      {q.trim().length >= 2 && !results.length && <p className="text-xs mt-2" style={{ color: "var(--sj-text-3)" }}>No members found who aren't already on the team.</p>}
      {team.invites.length > 0 && (
        <div className="mt-3">
          <div className="text-xs font-semibold mb-1 flex items-center gap-1" style={{ color: "var(--sj-text-3)" }}><MailCheck className="h-3.5 w-3.5" /> Waiting for a reply</div>
          <div className="flex flex-wrap gap-2">
            {team.invites.map((i) => (
              <span key={i.id} data-testid={`team-pending-invite-${i.id}`} className="inline-flex items-center gap-1 text-xs rounded-full pl-3 pr-1 py-1" style={{ background: "#0B5E4E14", color: "var(--sj-primary)" }}>
                {i.member_name}
                <button data-testid={`team-cancel-invite-${i.id}`} onClick={() => cancel(i)} title="Cancel invitation" className="p-0.5 rounded-full hover:bg-black/10"><X className="h-3 w-3" /></button>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const MyTeamInvites = ({ onChange }) => {
  const [invites, setInvites] = useState([]);
  const load = () => api.get("/portal/team-invites").then(({ data }) => setInvites(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const respond = (i, action) => api.post(`/portal/team-invites/${i.id}/${action}`)
    .then(() => { toast.success(action === "accept" ? `Welcome to ${i.team_name}!` : "Invitation declined"); load(); onChange?.(); }).catch(err);
  if (!invites.length) return null;
  return (
    <div className="space-y-2" data-testid="my-team-invites">
      {invites.map((i) => (
        <div key={i.id} data-testid={`my-invite-${i.id}`} className="rounded-2xl border p-4 flex flex-wrap items-center gap-3" style={{ background: "#FEF6D8", borderColor: "#F3DE8A" }}>
          <UserPlus className="h-5 w-5 shrink-0" style={{ color: "#8a6a00" }} />
          <p className="text-sm flex-1 min-w-[12rem]" style={{ color: "#3d3000" }}><strong>{i.invited_by}</strong> invited you to join <strong>{i.team_name}</strong>.</p>
          <button data-testid={`my-invite-accept-${i.id}`} onClick={() => respond(i, "accept")} className="rounded-full px-4 py-1.5 text-sm font-semibold text-white" style={{ background: "var(--sj-primary)" }}>Accept</button>
          <button data-testid={`my-invite-decline-${i.id}`} onClick={() => respond(i, "decline")} className="rounded-full px-4 py-1.5 text-sm font-semibold border bg-white" style={{ borderColor: "var(--sj-border)" }}>Decline</button>
        </div>
      ))}
    </div>
  );
};
